import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Heading,
  Progress,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../SEO";
import {
  clearHexacoWebSession,
  createCheckout,
  getDownloadUrl,
  getHexacoWebSession,
  getOrderStatus,
  HexacoWebOrderStatus,
} from "../../services/hexacoWebApi";
import {
  getApiErrorMessage,
  getHexacoReportDownloadFilename,
  getStatusLabelKey,
  isPaymentPhaseStatus,
  isPrePaymentProcessingStatus,
} from "./hexacoPurchaseUtils";
import { HEXACO_PURCHASE_INVALID_PDF_NAV_STATE } from "./hexacoPurchaseNavigation";
import { HEXACO_REPORT_PRICE_HKD } from "../../constants/hexaco";
import { downloadFileFromUrl } from "../../utils/downloadFileFromUrl";

const POLL_INTERVAL_MS = 3000;
const PROCESSING_STALL_TIMEOUT_MS = 5 * 60 * 1000;
const PROCESSING_STALL_STATUSES: HexacoWebOrderStatus[] = [
  "EXTRACTING_SCORES",
  "ANALYZING",
  "GENERATING_PDF",
];
const TERMINAL_STATUSES: HexacoWebOrderStatus[] = [
  "REPORT_READY",
  "PAID",
  "FAILED",
  "EXPIRED",
  "REFUNDED",
];

/** After Stripe return, keep polling while webhook may still mark PAID. */
const POST_CHECKOUT_POLL_STATUSES: HexacoWebOrderStatus[] = [
  "REPORT_READY",
  "CHECKOUT_PENDING",
];

const isServiceUnavailableFailureStage = (
  failureStage: string | null,
): boolean => {
  const stage = (failureStage || "").toUpperCase();
  return stage === "PDF_GENERATION" || stage === "DETAILED_ANALYSIS";
};

/** FC / httpx / HTTP details that must not be shown to end users. */
const isTechnicalFailureMessage = (message: string | null): boolean => {
  const normalized = (message || "").trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized.includes("unauthorized") ||
    normalized.includes("client error") ||
    normalized.includes("server error") ||
    normalized.includes("fcapp.run") ||
    normalized.includes("httpx") ||
    normalized.includes("for url ") ||
    /\b40[13]\b/.test(normalized) ||
    /\b50[0-9]\b/.test(normalized) ||
    /https?:\/\//.test(normalized)
  );
};

const shouldShowServiceUnavailable = (
  status: HexacoWebOrderStatus | null,
  failureStage: string | null,
  failureMessage: string | null,
  processingStallTimedOut: boolean,
): boolean => {
  if (processingStallTimedOut) {
    return true;
  }
  if (status !== "FAILED") {
    return false;
  }
  return (
    isServiceUnavailableFailureStage(failureStage) ||
    isTechnicalFailureMessage(failureMessage)
  );
};

const isProcessingStallStatus = (
  status: HexacoWebOrderStatus,
): boolean => PROCESSING_STALL_STATUSES.includes(status);

const HexacoOrderStatus: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { orderId: routeOrderId } = useParams<{ orderId: string }>();
  const pollTimeoutRef = useRef<number | null>(null);
  const processingStallStartedAtRef = useRef<number | null>(null);
  const processingStallStatusRef = useRef<HexacoWebOrderStatus | null>(null);

  const [status, setStatus] = useState<HexacoWebOrderStatus | null>(null);
  const [failureStage, setFailureStage] = useState<string | null>(null);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [processingStallTimedOut, setProcessingStallTimedOut] = useState(false);

  const awaitingCheckoutConfirmation =
    searchParams.get("fromCheckout") === "1";
  const hexacoSession = getHexacoWebSession();
  const orderId = routeOrderId || hexacoSession.orderId;
  const accessToken = hexacoSession.accessToken;
  const customerName = hexacoSession.customerName;

  const clearPollTimeout = () => {
    if (pollTimeoutRef.current !== null) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const clearProcessingStallTimer = () => {
    processingStallStartedAtRef.current = null;
    processingStallStatusRef.current = null;
    setProcessingStallTimedOut(false);
  };

  const fetchStatus = useCallback(async (): Promise<{
    status: HexacoWebOrderStatus;
    failureStage: string | null;
    failureMessage: string | null;
  } | null> => {
    if (!orderId || !accessToken) {
      setError(t("hexaco.purchase.errors.sessionExpired"));
      setLoading(false);
      return null;
    }

    const response = await getOrderStatus(orderId, accessToken);
    const nextFailureStage = response.failureStage || null;
    const nextFailureMessage = response.failureMessage || null;
    setStatus(response.status);
    setFailureStage(nextFailureStage);
    setFailureMessage(nextFailureMessage);
    setLoading(false);
    return {
      status: response.status,
      failureStage: nextFailureStage,
      failureMessage: nextFailureMessage,
    };
  }, [accessToken, orderId, t]);

  const redirectToPurchaseWithInvalidPdfAlert = useCallback(() => {
    clearHexacoWebSession();
    navigate("/hexaco/purchase", {
      replace: true,
      state: HEXACO_PURCHASE_INVALID_PDF_NAV_STATE,
    });
  }, [navigate]);

  useEffect(() => {
    if (!awaitingCheckoutConfirmation || status !== "PAID" || !orderId) {
      return;
    }

    navigate(`/hexaco/order/${orderId}`, { replace: true });
  }, [awaitingCheckoutConfirmation, navigate, orderId, status]);

  useEffect(() => {
    const refreshAfterBfcacheReturn = (event: PageTransitionEvent) => {
      // Browser Back from Stripe often restores this page from bfcache with stale
      // REPORT_READY UI / Pay-button loading state, while the server may already
      // be CHECKOUT_PENDING.
      setActionLoading(false);
      if (!event.persisted) {
        return;
      }
      setError(null);
      setReloadKey((key) => key + 1);
    };

    window.addEventListener("pageshow", refreshAfterBfcacheReturn);
    return () => {
      window.removeEventListener("pageshow", refreshAfterBfcacheReturn);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    clearProcessingStallTimer();

    const shouldKeepPolling = (nextStatus: HexacoWebOrderStatus): boolean => {
      if (
        awaitingCheckoutConfirmation &&
        POST_CHECKOUT_POLL_STATUSES.includes(nextStatus)
      ) {
        return true;
      }

      return !TERMINAL_STATUSES.includes(nextStatus);
    };

    const updateProcessingStallTimer = (
      nextStatus: HexacoWebOrderStatus,
    ): boolean => {
      if (!isProcessingStallStatus(nextStatus)) {
        clearProcessingStallTimer();
        return false;
      }

      if (processingStallStatusRef.current !== nextStatus) {
        processingStallStatusRef.current = nextStatus;
        processingStallStartedAtRef.current = Date.now();
        setProcessingStallTimedOut(false);
      }

      const startedAt = processingStallStartedAtRef.current;
      if (
        startedAt !== null &&
        Date.now() - startedAt >= PROCESSING_STALL_TIMEOUT_MS
      ) {
        setProcessingStallTimedOut(true);
        return true;
      }

      return false;
    };

    const poll = async () => {
      try {
        const next = await fetchStatus();
        if (cancelled || !next) {
          return;
        }

        if (next.status === "FAILED") {
          clearProcessingStallTimer();
          if (
            isServiceUnavailableFailureStage(next.failureStage) ||
            isTechnicalFailureMessage(next.failureMessage)
          ) {
            return;
          }
          redirectToPurchaseWithInvalidPdfAlert();
          return;
        }

        if (updateProcessingStallTimer(next.status)) {
          return;
        }

        if (!shouldKeepPolling(next.status)) {
          return;
        }

        pollTimeoutRef.current = window.setTimeout(poll, POLL_INTERVAL_MS);
      } catch (pollError) {
        if (!cancelled) {
          setError(getApiErrorMessage(pollError, t));
          setLoading(false);
        }
      }
    };

    poll();

    return () => {
      cancelled = true;
      clearPollTimeout();
    };
  }, [
    awaitingCheckoutConfirmation,
    fetchStatus,
    redirectToPurchaseWithInvalidPdfAlert,
    reloadKey,
    t,
  ]);

  const handlePay = async () => {
    if (!orderId || !accessToken) {
      setError(t("hexaco.purchase.errors.sessionExpired"));
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const checkout = await createCheckout(orderId, accessToken);
      window.location.href = checkout.checkoutUrl;
    } catch (checkoutError) {
      setError(getApiErrorMessage(checkoutError, t));
      setActionLoading(false);
      // Status may already be CHECKOUT_PENDING from a previous Pay attempt.
      void fetchStatus().catch(() => undefined);
    }
  };

  const handleDownload = async () => {
    if (!orderId || !accessToken) {
      setError(t("hexaco.purchase.errors.sessionExpired"));
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const download = await getDownloadUrl(orderId, accessToken);
      await downloadFileFromUrl(
        download.downloadUrl,
        getHexacoReportDownloadFilename(customerName),
      );
    } catch (downloadError) {
      setError(getApiErrorMessage(downloadError, t));
    } finally {
      setActionLoading(false);
    }
  };

  const isConfirmingPayment =
    awaitingCheckoutConfirmation &&
    status !== null &&
    POST_CHECKOUT_POLL_STATUSES.includes(status);

  const isServiceUnavailableFailure = shouldShowServiceUnavailable(
    status,
    failureStage,
    failureMessage,
    processingStallTimedOut,
  );
  const isPaymentPhase =
    isPaymentPhaseStatus(status) || isServiceUnavailableFailure;
  const isProcessing =
    !processingStallTimedOut &&
    (status ? isPrePaymentProcessingStatus(status) : loading);
  const showPay =
    (status === "REPORT_READY" || status === "CHECKOUT_PENDING") &&
    !awaitingCheckoutConfirmation;
  const showDownload = status === "PAID";
  const showFailure =
    status === "EXPIRED" ||
    status === "REFUNDED" ||
    isServiceUnavailableFailure;

  const hasStatusError = Boolean(error) && !status && !loading;

  const failureAlertMessage = isServiceUnavailableFailure
    ? t("hexaco.purchase.status.serviceUnavailable")
    : failureMessage || (status ? t(getStatusLabelKey(status)) : null);

  const headerSubtitle = loading
    ? t("hexaco.purchase.status.loading")
    : isConfirmingPayment
      ? t("hexaco.purchase.status.confirmingPayment")
      : isServiceUnavailableFailure
        ? null
        : status
          ? t(getStatusLabelKey(status))
          : hasStatusError
            ? null
            : t("hexaco.purchase.subtitle");

  const handleRetryStatus = () => {
    clearPollTimeout();
    clearProcessingStallTimer();
    setError(null);
    setStatus(null);
    setFailureStage(null);
    setFailureMessage(null);
    setLoading(true);
    setReloadKey((key) => key + 1);
  };

  const handleBackToPurchase = () => {
    clearHexacoWebSession();
  };

  return (
    <>
      <SEO
        title={
          isPaymentPhase
            ? t("hexaco.purchase.order.seo.title")
            : t("hexaco.purchase.seo.title")
        }
        description={
          isPaymentPhase
            ? t("hexaco.purchase.order.seo.description")
            : t("hexaco.purchase.seo.description")
        }
        noindex
      />

      <Box bg="gray.50" py={{ base: 10, md: 14 }}>
        <Container maxW="720px">
          <VStack align="stretch" spacing={6}>
            <Box textAlign="center">
              {isPaymentPhase ? (
                <>
                  <Heading size="lg" color="gray.800" mb={2}>
                    {t("hexaco.purchase.order.title")}
                  </Heading>
                  {orderId && (
                    <Text fontSize="sm" color="gray.500">
                      {t("hexaco.purchase.order.reference", { orderId })}
                    </Text>
                  )}
                </>
              ) : (
                <>
                  <Text fontSize="3xl" mb={3}>
                    📄
                  </Text>
                  <Heading size="lg" color="gray.800" mb={3}>
                    {hasStatusError
                      ? t("hexaco.purchase.order.title")
                      : t("hexaco.purchase.title")}
                  </Heading>
                  {headerSubtitle && (
                    <Text color="gray.600" lineHeight="tall">
                      {headerSubtitle}
                    </Text>
                  )}
                </>
              )}
            </Box>

            <Box
              bg="white"
              borderRadius="2xl"
              boxShadow="sm"
              border="1px solid"
              borderColor="teal.100"
              px={{ base: 5, md: 8 }}
              py={{ base: 6, md: 8 }}
            >
              <VStack spacing={5} align="stretch">
                {loading && (
                  <VStack py={6} spacing={4}>
                    <Spinner size="lg" color="teal.500" />
                    <Text color="gray.600">
                      {t("hexaco.purchase.status.loading")}
                    </Text>
                    <Alert status="warning" borderRadius="lg" w="full">
                      <AlertIcon />
                      {t("hexaco.purchase.status.processingWarning")}
                    </Alert>
                  </VStack>
                )}

                {hasStatusError && (
                  <VStack spacing={4} align="stretch" py={2}>
                    <Alert status="error" borderRadius="lg">
                      <AlertIcon />
                      {error}
                    </Alert>
                    <Button
                      colorScheme="teal"
                      variant="outline"
                      borderRadius="full"
                      onClick={handleRetryStatus}
                    >
                      {t("hexaco.purchase.status.retry")}
                    </Button>
                  </VStack>
                )}

                {!loading && status && (
                  <>
                    {isConfirmingPayment && (
                      <VStack py={4} spacing={4} align="stretch">
                        <VStack spacing={3}>
                          <Spinner size="lg" color="teal.500" />
                          <Text
                            fontSize="lg"
                            fontWeight="semibold"
                            color="gray.800"
                            textAlign="center"
                          >
                            {t("hexaco.purchase.status.confirmingPayment")}
                          </Text>
                        </VStack>
                        <Progress
                          size="sm"
                          isIndeterminate
                          colorScheme="teal"
                          borderRadius="full"
                        />
                      </VStack>
                    )}

                    {isPaymentPhase &&
                      !isConfirmingPayment &&
                      !isServiceUnavailableFailure && (
                      <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                        {t(getStatusLabelKey(status))}
                      </Text>
                    )}

                    {isProcessing && (
                      <>
                        <Progress
                          size="sm"
                          isIndeterminate
                          colorScheme="teal"
                          borderRadius="full"
                        />
                        <Alert status="warning" borderRadius="lg">
                          <AlertIcon />
                          {t("hexaco.purchase.status.processingWarning")}
                        </Alert>
                        <Text fontSize="sm" color="gray.600" lineHeight="tall">
                          {t("hexaco.purchase.status.processingHint")}
                        </Text>
                      </>
                    )}

                    {showFailure && status !== "REFUNDED" && failureAlertMessage && (
                      <Alert status="error" borderRadius="lg">
                        <AlertIcon />
                        {failureAlertMessage}
                      </Alert>
                    )}

                    {showPay && (
                      <VStack spacing={3} align="stretch">
                        <Text color="gray.600" lineHeight="tall">
                          {t("hexaco.purchase.pay.description")}
                        </Text>
                        <Button
                          colorScheme="teal"
                          size="lg"
                          borderRadius="full"
                          onClick={handlePay}
                          isLoading={actionLoading}
                          loadingText={t("hexaco.purchase.pay.loading")}
                        >
                          {t("hexaco.purchase.pay.button", {
                            price: HEXACO_REPORT_PRICE_HKD,
                          })}
                        </Button>
                      </VStack>
                    )}

                    {showDownload && (
                      <VStack spacing={3} align="stretch">
                        <Alert status="success" borderRadius="lg">
                          <AlertIcon />
                          {t("hexaco.purchase.download.emailNotice")}
                        </Alert>
                        <Button
                          colorScheme="teal"
                          size="lg"
                          borderRadius="full"
                          onClick={handleDownload}
                          isLoading={actionLoading}
                          loadingText={t("hexaco.purchase.download.loading")}
                        >
                          {t("hexaco.purchase.download.button")}
                        </Button>
                      </VStack>
                    )}

                    {status === "REFUNDED" && (
                      <Alert status="info" borderRadius="lg">
                        <AlertIcon />
                        {t("hexaco.purchase.status.refunded")}
                      </Alert>
                    )}
                  </>
                )}

                {!loading && status && error && !isServiceUnavailableFailure && (
                  <Alert status="error" borderRadius="lg">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
              </VStack>
            </Box>

            {!isProcessing && !isConfirmingPayment && (
              <Button
                as={RouterLink}
                to="/hexaco/purchase"
                variant="ghost"
                colorScheme="teal"
                alignSelf="center"
                onClick={handleBackToPurchase}
              >
                {t("hexaco.purchase.order.backToPurchase")}
              </Button>
            )}
          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default HexacoOrderStatus;
